import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';

const DISCORD_EVENT_OPTIONS = [
  { key: 'live-stream', label: 'Live-Stream' },
  { key: 'lurker2-live-need', label: 'Lurker2: Stream live (wenig Viewer)' },
  { key: 'waitlist-new', label: 'Warteliste: neuer Eintrag' },
  { key: 'waitlist-approved', label: 'Warteliste: freigeschaltet' },
  { key: 'apn-clip-new', label: 'AP‑N Clips: neuer Upload/Link' },
  { key: 'feature-request-new', label: 'Feature‑Wunsch: neuer Eintrag' },
  { key: 'feature-request-comment-new', label: 'Feature‑Wunsch: neuer Kommentar' },
  { key: 'mentor-application-new', label: 'Mentoren: neue Bewerbung' },
  { key: 'knowledge-entry-new', label: 'Wissensdatenbank: neuer Eintrag' },
  { key: 'birthday-today', label: 'Community: Geburtstag heute' },
];

const PLACEHOLDERS_BY_EVENT = {
  'live-stream': [
    { label: 'Name', token: '{{displayName}}' },
    { label: 'Link', token: '{{streamUrl}}' },
    { label: 'Plattform', token: '{{platform}}' },
    { label: 'Titel', token: '{{streamTitle}}' },
    { label: 'Beschreibung', token: '{{streamDescription}}' },
    { label: 'Startzeit', token: '{{streamStart}}' },
    { label: 'Endzeit', token: '{{streamEnd}}' },
  ],
  'lurker2-live-need': [
    { label: 'Name', token: '{{displayName}}' },
    { label: 'Link', token: '{{streamUrl}}' },
    { label: 'Plattform', token: '{{platform}}' },
    { label: 'Twitch Login', token: '{{twitchLogin}}' },
    { label: 'Viewer', token: '{{viewerCount}}' },
    { label: 'Kategorie', token: '{{gameName}}' },
    { label: 'Titel', token: '{{streamTitle}}' },
    { label: 'Beschreibung', token: '{{streamDescription}}' },
    { label: 'Startzeit', token: '{{streamStart}}' },
    { label: 'Endzeit', token: '{{streamEnd}}' },
  ],
  'waitlist-new': [
    { label: 'Username', token: '{{username}}' },
    { label: 'E‑Mail', token: '{{email}}' },
    { label: 'Quelle', token: '{{discoverySource}}' },
    { label: 'ReferredBy', token: '{{referredBy}}' },
    { label: 'AGB Zeit', token: '{{acceptedTermsAt}}' },
  ],
  'waitlist-approved': [
    { label: 'User ID', token: '{{userId}}' },
    { label: 'Username', token: '{{username}}' },
    { label: 'Name', token: '{{displayName}}' },
    { label: 'E‑Mail', token: '{{email}}' },
    { label: 'Quelle', token: '{{discoverySource}}' },
    { label: 'ReferredBy', token: '{{referredBy}}' },
    { label: 'AGB Zeit', token: '{{acceptedTermsAt}}' },
    { label: 'Freigeschaltet am', token: '{{approvedAt}}' },
    { label: 'Freigeschaltet durch', token: '{{approvedBy}}' },
    { label: 'Freischalter User ID', token: '{{approvedByUserId}}' },
  ],
  'apn-clip-new': [
    { label: 'ID', token: '{{submissionId}}' },
    { label: 'Creator', token: '{{creatorName}}' },
    { label: 'Plattform', token: '{{platform}}' },
    { label: 'Kategorie', token: '{{category}}' },
    { label: 'Titel', token: '{{title}}' },
    { label: 'Modus', token: '{{mode}}' },
    { label: 'Source URL', token: '{{sourceUrl}}' },
    { label: 'Dateiname', token: '{{originalFilename}}' },
  ],
  'feature-request-new': [
    { label: 'ID', token: '{{featureRequestId}}' },
    { label: 'Slug', token: '{{slug}}' },
    { label: 'Titel', token: '{{title}}' },
    { label: 'Typ', token: '{{type}}' },
    { label: 'Autor', token: '{{authorName}}' },
  ],
  'feature-request-comment-new': [
    { label: 'Eintrag ID', token: '{{featureRequestId}}' },
    { label: 'Eintrag', token: '{{featureRequestTitle}}' },
    { label: 'Kommentar ID', token: '{{commentId}}' },
    { label: 'Autor', token: '{{authorName}}' },
    { label: 'Snippet', token: '{{commentSnippet}}' },
  ],
  'mentor-application-new': [
    { label: 'Bewerbung ID', token: '{{applicationId}}' },
    { label: 'User ID', token: '{{userId}}' },
    { label: 'Name', token: '{{authorName}}' },
    { label: 'Bereiche', token: '{{supportAreas}}' },
  ],
  'knowledge-entry-new': [
    { label: 'Entry ID', token: '{{entryId}}' },
    { label: 'Titel', token: '{{title}}' },
    { label: 'Slug', token: '{{slug}}' },
    { label: 'Typ', token: '{{type}}' },
    { label: 'Status', token: '{{status}}' },
    { label: 'Autor', token: '{{authorName}}' },
  ],
  'birthday-today': [
    { label: 'User ID', token: '{{userId}}' },
    { label: 'Username', token: '{{username}}' },
    { label: 'Name', token: '{{displayName}}' },
    { label: 'Alter', token: '{{age}}' },
    { label: 'Geburtstag', token: '{{birthday}}' },
  ],
};

function applyWrapFormat({ text, selectionStart, selectionEnd }, before, after) {
  const start = Math.max(0, Number(selectionStart || 0));
  const end = Math.max(start, Number(selectionEnd || 0));
  const selected = text.slice(start, end);
  const next = text.slice(0, start) + before + selected + after + text.slice(end);
  const nextStart = start + before.length;
  const nextEnd = nextStart + selected.length;
  return { next, nextSelectionStart: nextStart, nextSelectionEnd: nextEnd };
}

function applyLinePrefix({ text, selectionStart, selectionEnd }, prefix) {
  const start = Math.max(0, Number(selectionStart || 0));
  const end = Math.max(start, Number(selectionEnd || 0));
  const beforeText = text.slice(0, start);
  const selection = text.slice(start, end);
  const afterText = text.slice(end);

  // Apply prefix to each selected line; if no selection, apply to current line.
  const target = selection || '';
  const lines = target.split('\n');
  const prefixed = lines.map(l => (l.startsWith(prefix) ? l : prefix + l)).join('\n');

  const next = beforeText + prefixed + afterText;
  const nextStart = start;
  const nextEnd = start + prefixed.length;
  return { next, nextSelectionStart: nextStart, nextSelectionEnd: nextEnd };
}

function FormatToolbar({ onAction, disabled }) {
  const btn = {
    background: '#2f2f2f',
    color: '#ddd',
    border: '1px solid #555',
    padding: '6px 10px',
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: 12,
    lineHeight: 1,
  };
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
      <button type="button" disabled={disabled} onClick={() => onAction('bold')} style={btn}><b>B</b></button>
      <button type="button" disabled={disabled} onClick={() => onAction('italic')} style={btn}><i>I</i></button>
      <button type="button" disabled={disabled} onClick={() => onAction('underline')} style={btn}><span style={{ textDecoration: 'underline' }}>U</span></button>
      <button type="button" disabled={disabled} onClick={() => onAction('strike')} style={btn}><span style={{ textDecoration: 'line-through' }}>S</span></button>
      <button type="button" disabled={disabled} onClick={() => onAction('code')} style={btn}>`code`</button>
      <button type="button" disabled={disabled} onClick={() => onAction('codeblock')} style={btn}>```</button>
      <button type="button" disabled={disabled} onClick={() => onAction('link')} style={btn}>Link</button>
      <div style={{ fontSize: 12, opacity: 0.65 }}>
        Discord nutzt Markdown.
      </div>
    </div>
  );
}

function insertAtSelection({ text, selectionStart, selectionEnd }, insertText) {
  const start = Math.max(0, Number(selectionStart || 0));
  const end = Math.max(start, Number(selectionEnd || 0));
  const next = text.slice(0, start) + insertText + text.slice(end);
  const caret = start + String(insertText).length;
  return { next, nextSelectionStart: caret, nextSelectionEnd: caret };
}

function PlaceholderToolbar({ onInsert, disabled, items }) {
  const btn = {
    background: '#2f2f2f',
    color: '#ddd',
    border: '1px solid #555',
    padding: '6px 10px',
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: 12,
    lineHeight: 1,
  };
  const list = Array.isArray(items) ? items : [];

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
      <div style={{ fontSize: 12, opacity: 0.7, marginRight: 4 }}>Platzhalter:</div>
      {list.map(it => (
        <button key={it.token} type="button" disabled={disabled} onClick={() => onInsert(it.token)} style={btn}>
          {it.label}
        </button>
      ))}
    </div>
  );
}

export default function DiscordWebhook() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [webhooks, setWebhooks] = useState([]);
  const [messages, setMessages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState('');

  const [selectedWebhookId, setSelectedWebhookId] = useState(null);
  const [webhookKey, setWebhookKey] = useState('');
  const [webhookName, setWebhookName] = useState('');
  const [url, setUrl] = useState('');
  const [enabled, setEnabled] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState('');
  const [createName, setCreateName] = useState('');
  const [createUrl, setCreateUrl] = useState('');
  const [createEnabled, setCreateEnabled] = useState(true);

  const [selectedEventKey, setSelectedEventKey] = useState('live-stream');

  const [msgModalOpen, setMsgModalOpen] = useState(false);
  const [newMsgTitle, setNewMsgTitle] = useState('');
  const [newMsgContent, setNewMsgContent] = useState('');
  const newMsgContentRef = useRef(null);
  const [newMsgAppendHere, setNewMsgAppendHere] = useState(false);
  const [workingMsgId, setWorkingMsgId] = useState(null);
  const [workingMsgTitle, setWorkingMsgTitle] = useState('');
  const [workingMsgContent, setWorkingMsgContent] = useState('');
  const workingMsgContentRef = useRef(null);
  const [workingMsgAppendHere, setWorkingMsgAppendHere] = useState(false);
  const [workingMsgEventKey, setWorkingMsgEventKey] = useState('live-stream');
  const [busyMsg, setBusyMsg] = useState(false);

  const selectedWebhook = useMemo(() => {
    if (!Array.isArray(webhooks) || webhooks.length === 0) return null;
    const fromState = webhooks.find(w => Number(w.id) === Number(selectedWebhookId));
    return fromState || webhooks[0];
  }, [webhooks, selectedWebhookId]);

  const placeholderItems = useMemo(() => {
    return PLACEHOLDERS_BY_EVENT[selectedEventKey] || [];
  }, [selectedEventKey]);

  const filteredMessages = useMemo(() => {
    const ek = selectedEventKey || 'live-stream';
    const list = Array.isArray(messages) ? messages : [];
    if (ek === 'live-stream') {
      return list.filter(m => (m?.eventKey == null ? 'live-stream' : m.eventKey) === 'live-stream');
    }
    return list.filter(m => (m?.eventKey || 'live-stream') === ek);
  }, [messages, selectedEventKey]);

  const assignedIds = useMemo(() => {
    const ids = selectedWebhook?.messageIds;
    return Array.isArray(ids) ? ids.map(n => Number(n)).filter(Boolean) : [];
  }, [selectedWebhook]);

  async function reload() {
    setLoading(true);
    setError('');
    setTestResult('');
    try {
      const [hooksRes, msgsRes] = await Promise.all([
        api.get('/discord-webhooks'),
        api.get('/discord-webhooks/messages/all')
      ]);
      const hooks = Array.isArray(hooksRes.data) ? hooksRes.data : [];
      const msgs = Array.isArray(msgsRes.data) ? msgsRes.data : [];
      setWebhooks(hooks);
      setMessages(msgs);

      const nextId = hooks.some(h => Number(h.id) === Number(selectedWebhookId))
        ? selectedWebhookId
        : (hooks[0]?.id || null);
      setSelectedWebhookId(nextId);

      const sel = hooks.find(h => Number(h.id) === Number(nextId)) || hooks[0];
      setWebhookKey(sel?.key || '');
      setWebhookName(sel?.name || '');
      setUrl(sel?.url || '');
      setEnabled(sel?.enabled !== false);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Laden fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function saveWebhook() {
    if (!selectedWebhook?.id) return;
    setSaving(true);
    setError('');
    setTestResult('');
    try {
      await api.post(`/discord-webhooks/${selectedWebhook.id}`, { name: (webhookName || '').trim() || null, url: url || null, enabled: !!enabled });
      await reload();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  function selectWebhook(id) {
    const nextId = id ? Number(id) : null;
    setSelectedWebhookId(nextId);
    const hook = (Array.isArray(webhooks) ? webhooks : []).find(w => Number(w.id) === Number(nextId));
    if (hook) {
      setWebhookKey(hook.key || '');
      setWebhookName(hook.name || '');
      setUrl(hook.url || '');
      setEnabled(hook.enabled !== false);
    }
  }

  async function createWebhook() {
    const key = (createKey || '').trim();
    const name = (createName || '').trim();
    setSaving(true);
    setError('');
    setTestResult('');
    try {
      const res = await api.post('/discord-webhooks/create', { key, name, url: (createUrl || '').trim() || null, enabled: !!createEnabled });
      const created = res.data;
      setCreateKey('');
      setCreateName('');
      setCreateUrl('');
      setCreateEnabled(true);
      setCreateOpen(false);
      await reload();
      if (created?.id) setSelectedWebhookId(created.id);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Erstellen fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  async function deleteWebhook() {
    if (!selectedWebhook?.id) return;
    if (!confirm('Webhook wirklich löschen? (Zuweisungen werden entfernt)')) return;
    setSaving(true);
    setError('');
    setTestResult('');
    try {
      await api.post(`/discord-webhooks/${selectedWebhook.id}/delete`);
      setSelectedWebhookId(null);
      await reload();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Löschen fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  async function runTest() {
    if (!selectedWebhook?.id) return;
    setTesting(true);
    setError('');
    setTestResult('');
    try {
      const res = await api.post(`/discord-webhooks/${selectedWebhook.id}/test`);
      const mid = res.data?.messageId;
      setTestResult(`Test gesendet (Nachricht #${mid}).`);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Test fehlgeschlagen.');
    } finally {
      setTesting(false);
    }
  }

  async function createMessage() {
    const content = (newMsgContent || '').trim();
    if (!content) return;
    setBusyMsg(true);
    setError('');
    try {
      await api.post('/discord-webhooks/messages', { eventKey: selectedEventKey, title: (newMsgTitle || '').trim() || null, content, appendHere: !!newMsgAppendHere });
      setNewMsgTitle('');
      setNewMsgContent('');
      setNewMsgAppendHere(false);
      await reload();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Nachricht erstellen fehlgeschlagen.');
    } finally {
      setBusyMsg(false);
    }
  }

  function startEditMessage(m) {
    setWorkingMsgId(m?.id || null);
    setWorkingMsgTitle(m?.title || '');
    setWorkingMsgContent(m?.content || '');
    setWorkingMsgAppendHere(!!m?.appendHere);
    setWorkingMsgEventKey(m?.eventKey || 'live-stream');
  }

  async function saveEditMessage() {
    if (!workingMsgId) return;
    const content = (workingMsgContent || '').trim();
    if (!content) return;
    setBusyMsg(true);
    setError('');
    try {
      await api.post(`/discord-webhooks/messages/${workingMsgId}`, { eventKey: workingMsgEventKey, title: (workingMsgTitle || '').trim() || null, content, appendHere: !!workingMsgAppendHere });
      setWorkingMsgId(null);
      setWorkingMsgTitle('');
      setWorkingMsgContent('');
      setWorkingMsgAppendHere(false);
      setWorkingMsgEventKey('live-stream');
      await reload();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setBusyMsg(false);
    }
  }

  async function deleteMessage(id) {
    if (!id) return;
    if (!confirm('Nachricht wirklich löschen?')) return;
    setBusyMsg(true);
    setError('');
    try {
      await api.post(`/discord-webhooks/messages/${id}/delete`);
      if (workingMsgId === id) {
        setWorkingMsgId(null);
        setWorkingMsgTitle('');
        setWorkingMsgContent('');
        setWorkingMsgAppendHere(false);
        setWorkingMsgEventKey('live-stream');
      }
      await reload();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Löschen fehlgeschlagen.');
    } finally {
      setBusyMsg(false);
    }
  }

  async function setAssigned(messageId, shouldAssign) {
    if (!selectedWebhook?.id || !messageId) return;
    setBusyMsg(true);
    setError('');
    try {
      if (shouldAssign) {
        await api.post(`/discord-webhooks/${selectedWebhook.id}/messages/assign`, { messageId });
      } else {
        await api.post(`/discord-webhooks/${selectedWebhook.id}/messages/unassign`, { messageId });
      }
      await reload();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Zuweisung fehlgeschlagen.');
    } finally {
      setBusyMsg(false);
    }
  }

  function runFormatForTextarea(ref, currentValue, setValue, action) {
    const el = ref?.current;
    if (!el) return;

    const snapshot = {
      text: String(currentValue || ''),
      selectionStart: el.selectionStart,
      selectionEnd: el.selectionEnd,
    };

    let result;
    if (action === 'bold') result = applyWrapFormat(snapshot, '**', '**');
    else if (action === 'italic') result = applyWrapFormat(snapshot, '*', '*');
    else if (action === 'underline') result = applyWrapFormat(snapshot, '__', '__');
    else if (action === 'strike') result = applyWrapFormat(snapshot, '~~', '~~');
    else if (action === 'code') result = applyWrapFormat(snapshot, '`', '`');
    else if (action === 'codeblock') result = applyWrapFormat(snapshot, '```\n', '\n```');
    else if (action === 'link') {
      // Keep it simple: use selection as link-text.
      result = applyWrapFormat(snapshot, '[', '](https://example.com)');
    } else {
      return;
    }

    setValue(result.next);
    setTimeout(() => {
      try {
        el.focus();
        el.setSelectionRange(result.nextSelectionStart, result.nextSelectionEnd);
      } catch {}
    }, 0);
  }

  function runInsertForTextarea(ref, currentValue, setValue, insertText) {
    const el = ref?.current;
    if (!el) return;
    const snapshot = {
      text: String(currentValue || ''),
      selectionStart: el.selectionStart,
      selectionEnd: el.selectionEnd,
    };
    const result = insertAtSelection(snapshot, String(insertText || ''));
    setValue(result.next);
    setTimeout(() => {
      try {
        el.focus();
        el.setSelectionRange(result.nextSelectionStart, result.nextSelectionEnd);
      } catch {}
    }, 0);
  }

  return (
    <div className="home-page">
      {/* Hero header */}
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Discord-Webhook</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign: 'center', maxWidth:760 }}>
          Hier kannst du Discord-Webhooks verwalten und Testnachrichten versenden.
        </p>
      </section>

      {/* Centered content */}
      <section style={{ width: '100%', maxWidth: 900, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        {loading && <div style={{ opacity: 0.8 }}>Lade…</div>}
        {!loading && error && <div style={{ color: '#ff8484', marginBottom: 12 }}>{error}</div>}
        {!loading && testResult && <div style={{ color: '#9fe7b2', marginBottom: 12 }}>{testResult}</div>}

        {!loading && selectedWebhook && (
          <div style={{ background: '#1e1e1e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 18 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12, flexWrap:'wrap', marginBottom: 10 }}>
              <div style={{ display:'flex', gap: 10, alignItems:'center', flexWrap:'wrap' }}>
                <div style={{ fontWeight: 900 }}>Webhook</div>
                <select
                  value={selectedWebhook?.id || ''}
                  onChange={(e) => selectWebhook(e.target.value)}
                  style={{ background:'#111', color:'#fff', border:'1px solid #333', borderRadius:10, padding:'8px 10px', fontSize:12, minWidth: 260 }}
                >
                  {(Array.isArray(webhooks) ? webhooks : []).map(w => (
                    <option key={w.id} value={w.id}>{w.name || w.key || `#${w.id}`}</option>
                  ))}
                </select>
              </div>

              <div style={{ display:'flex', gap: 10, alignItems:'center', flexWrap:'wrap' }}>
                <button
                  type="button"
                  onClick={() => setCreateOpen(v => !v)}
                  style={{ background:'#2f2f2f', color:'#ddd', border:'1px solid #555', padding:'8px 12px', borderRadius:10, cursor:'pointer' }}
                >
                  {createOpen ? 'Erstellen schließen' : 'Neuen Webhook'}
                </button>
                <button
                  type="button"
                  onClick={deleteWebhook}
                  disabled={saving || selectedWebhook?.key === 'live-stream'}
                  title={selectedWebhook?.key === 'live-stream' ? 'Der Standard-Webhook kann nicht gelöscht werden.' : 'Webhook löschen'}
                  style={{ background:'#2f2f2f', color:'#ddd', border:'1px solid #555', padding:'8px 12px', borderRadius:10, cursor:'pointer', opacity: (saving || selectedWebhook?.key === 'live-stream') ? 0.5 : 1 }}
                >
                  Löschen
                </button>
              </div>
            </div>

            <div style={{ fontSize: 12, opacity: 0.65, marginBottom: 12 }}>
              Key: <b>{webhookKey || selectedWebhook.key || '-'}</b>
            </div>

            {createOpen && (
              <div style={{ background:'#151515', border:'1px solid #2d2d2d', borderRadius:12, padding: 12, marginBottom: 14 }}>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>Neuen Webhook anlegen</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Key</div>
                    <input
                      type="text"
                      value={createKey}
                      onChange={(e) => setCreateKey(e.target.value)}
                      placeholder="z.B. events-mod" 
                      style={{ width:'100%', background:'#111', color:'#fff', border:'1px solid #333', borderRadius:10, padding:'10px 12px', fontSize:13 }}
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Name</div>
                    <input
                      type="text"
                      value={createName}
                      onChange={(e) => setCreateName(e.target.value)}
                      placeholder="Anzeige-Name" 
                      style={{ width:'100%', background:'#111', color:'#fff', border:'1px solid #333', borderRadius:10, padding:'10px 12px', fontSize:13 }}
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Webhook-URL (optional)</div>
                  <input
                    type="text"
                    value={createUrl}
                    onChange={(e) => setCreateUrl(e.target.value)}
                    placeholder="https://discord.com/api/webhooks/..."
                    style={{ width:'100%', background:'#111', color:'#fff', border:'1px solid #333', borderRadius:10, padding:'10px 12px', fontSize:13 }}
                    autoComplete="off"
                  />
                </div>
                <div style={{ display:'flex', gap: 12, alignItems:'center', flexWrap:'wrap', marginTop: 10 }}>
                  <label style={{ display:'flex', alignItems:'center', gap:8, fontSize: 13, opacity: 0.9 }}>
                    <input type="checkbox" checked={!!createEnabled} onChange={(e) => setCreateEnabled(e.target.checked)} /> aktiviert
                  </label>
                  <button
                    type="button"
                    onClick={createWebhook}
                    disabled={saving || !(createKey || '').trim() || !(createName || '').trim()}
                    style={{ background:'#2b2b2b', color:'#fff', border:'1px solid #444', borderRadius:10, padding:'10px 14px', cursor:'pointer', opacity: (saving || !(createKey || '').trim() || !(createName || '').trim()) ? 0.6 : 1 }}
                  >
                    {saving ? 'Erstelle…' : 'Erstellen'}
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display:'flex', alignItems:'center', gap:8, fontSize: 13, opacity: 0.9 }}>
                <input type="checkbox" checked={!!enabled} onChange={(e) => setEnabled(e.target.checked)} /> aktiviert
              </label>

              <button
                type="button"
                onClick={() => setMsgModalOpen(true)}
                style={{ background:'#2f2f2f', color:'#ddd', border:'1px solid #555', padding:'8px 12px', borderRadius:10, cursor:'pointer' }}
              >
                Nachrichten verwalten
              </button>

              <button
                type="button"
                onClick={runTest}
                disabled={testing}
                style={{ background:'#32CD89', color:'#032d1e', fontWeight:800, border:'1px solid #32CD89', padding:'8px 12px', borderRadius:10, cursor:'pointer' }}
                title="Sendet eine zufällige zugewiesene Nachricht an den Webhook"
              >
                {testing ? 'Teste…' : 'Test senden'}
              </button>
            </div>

            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Name</div>
              <input
                type="text"
                value={webhookName}
                onChange={(e) => setWebhookName(e.target.value)}
                placeholder="Anzeige-Name"
                style={{ width:'100%', background:'#111', color:'#fff', border:'1px solid #333', borderRadius:10, padding:'12px 14px', fontSize:14, marginBottom: 10 }}
                autoComplete="off"
              />
              <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Webhook-URL</div>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://discord.com/api/webhooks/..."
                style={{ width:'100%', background:'#111', color:'#fff', border:'1px solid #333', borderRadius:10, padding:'12px 14px', fontSize:14 }}
                autoComplete="off"
              />
              <div style={{ display:'flex', gap: 10, marginTop: 10, alignItems:'center', flexWrap:'wrap' }}>
                <button
                  type="button"
                  onClick={saveWebhook}
                  disabled={saving}
                  style={{ background:'#2b2b2b', color:'#fff', border:'1px solid #444', borderRadius:10, padding:'10px 14px', cursor:'pointer', minWidth: 140 }}
                >
                  {saving ? 'Speichere…' : 'Speichern'}
                </button>
                <div style={{ fontSize: 12, opacity: 0.65 }}>
                  Zugewiesene Nachrichten: <b>{assignedIds.length}</b>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !selectedWebhook && (
          <div style={{ background: '#1e1e1e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 18 }}>
            Kein Webhook gefunden.
          </div>
        )}
      </section>

      {msgModalOpen && (
        <div
          onClick={() => setMsgModalOpen(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex: 2500, display:'flex', alignItems:'center', justifyContent:'center', padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width:'min(920px, 96vw)', maxHeight:'88vh', overflow:'auto', background:'#1c1c1c', border:'1px solid #333', borderRadius:14, boxShadow:'0 8px 30px rgba(0,0,0,0.5)', padding: 16 }}
          >
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 10, marginBottom: 10 }}>
              <div style={{ fontWeight: 900 }}>Discord Nachrichten</div>
              <button type="button" onClick={() => setMsgModalOpen(false)} style={{ background:'#333', color:'#fff', border:'1px solid #444', borderRadius:10, padding:'8px 10px', cursor:'pointer' }}>
                Schließen
              </button>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr', gap: 14 }}>
              <div style={{ background:'#151515', border:'1px solid #2d2d2d', borderRadius:12, padding: 12 }}>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>Event</div>
                <select
                  value={selectedEventKey}
                  onChange={(e) => setSelectedEventKey(e.target.value)}
                  style={{ width:'100%', background:'#111', color:'#fff', border:'1px solid #333', borderRadius:10, padding:'10px 12px', fontSize:13 }}
                >
                  {DISCORD_EVENT_OPTIONS.map(o => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
                <div style={{ fontSize: 12, opacity: 0.65, marginTop: 8 }}>
                  Nachrichten werden pro Event erstellt und dem ausgewählten Webhook zugewiesen.
                </div>
              </div>

              <div style={{ background:'#151515', border:'1px solid #2d2d2d', borderRadius:12, padding: 12 }}>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>Neue Nachricht</div>
                <input
                  type="text"
                  value={newMsgTitle}
                  onChange={(e)=> setNewMsgTitle(e.target.value)}
                  placeholder="Titel (optional)"
                  style={{ width:'100%', background:'#111', color:'#fff', border:'1px solid #333', borderRadius:10, padding:'10px 12px', fontSize:13, marginBottom: 8 }}
                />
                <FormatToolbar
                  disabled={busyMsg}
                  onAction={(action) => runFormatForTextarea(newMsgContentRef, newMsgContent, setNewMsgContent, action)}
                />
                <PlaceholderToolbar
                  disabled={busyMsg}
                  onInsert={(token) => runInsertForTextarea(newMsgContentRef, newMsgContent, setNewMsgContent, token)}
                  items={placeholderItems}
                />
                <textarea
                  ref={newMsgContentRef}
                  value={newMsgContent}
                  onChange={(e)=> setNewMsgContent(e.target.value)}
                  placeholder="Nachricht (Markdown für Discord)…"
                  rows={5}
                  style={{ width:'100%', background:'#111', color:'#fff', border:'1px solid #333', borderRadius:10, padding:'10px 12px', fontSize:13, resize:'vertical' }}
                />
                <div style={{ display:'flex', gap: 10, marginTop: 10, alignItems:'center' }}>
                  <button
                    type="button"
                    onClick={createMessage}
                    disabled={busyMsg || !(newMsgContent || '').trim()}
                    style={{ background:'#2b2b2b', color:'#fff', border:'1px solid #444', borderRadius:10, padding:'10px 14px', cursor:'pointer' }}
                  >
                    {busyMsg ? 'Bitte warten…' : 'Hinzufügen'}
                  </button>
                  <div style={{ fontSize: 12, opacity: 0.65 }}>Diese Nachricht kann anschließend dem Webhook zugewiesen werden.</div>
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, opacity: 0.9 }}>
                  <input
                    type="checkbox"
                    checked={!!newMsgAppendHere}
                    onChange={(e) => setNewMsgAppendHere(e.target.checked)}
                    disabled={busyMsg}
                  />
                  @here anhängen (mit Leerzeile davor)
                </label>
              </div>

              <div style={{ background:'#151515', border:'1px solid #2d2d2d', borderRadius:12, padding: 12 }}>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>Vorhandene Nachrichten</div>
                {filteredMessages.length === 0 ? (
                  <div style={{ opacity: 0.7 }}>Noch keine Nachrichten.</div>
                ) : (
                  <div style={{ display:'grid', gap: 10 }}>
                    {filteredMessages.map(m => {
                      const isAssigned = assignedIds.includes(Number(m.id));
                      const isEditing = Number(workingMsgId) === Number(m.id);
                      return (
                        <div key={m.id} style={{ border:'1px solid rgba(255,255,255,0.08)', borderRadius:12, padding: 12, background:'#101010' }}>
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 10, marginBottom: 8, flexWrap:'wrap' }}>
                            <div style={{ fontWeight: 800, minWidth: 0 }}>
                              {m.title ? m.title : `Nachricht #${m.id}`}
                            </div>
                            <div style={{ display:'flex', gap: 8, alignItems:'center', flexWrap:'wrap' }}>
                              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize: 12, opacity: 0.9 }}>
                                <input
                                  type="checkbox"
                                  checked={isAssigned}
                                  onChange={(e) => setAssigned(m.id, e.target.checked)}
                                  disabled={busyMsg || !selectedWebhook}
                                />
                                dem Webhook zuweisen
                              </label>
                              {!isEditing ? (
                                <button
                                  type="button"
                                  onClick={() => startEditMessage(m)}
                                  style={{ background:'#1f2744', color:'#9fbaff', border:'1px solid #2f3e66', padding:'6px 10px', borderRadius:10, cursor:'pointer', fontSize:12 }}
                                >
                                  Bearbeiten
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => { setWorkingMsgId(null); setWorkingMsgTitle(''); setWorkingMsgContent(''); setWorkingMsgAppendHere(false); setWorkingMsgEventKey('live-stream'); }}
                                  style={{ background:'#2f2f2f', color:'#ddd', border:'1px solid #555', padding:'6px 10px', borderRadius:10, cursor:'pointer', fontSize:12 }}
                                >
                                  Abbrechen
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => deleteMessage(m.id)}
                                disabled={busyMsg}
                                style={{ background:'#441f20', color:'#ff8484', border:'1px solid #662d30', padding:'6px 10px', borderRadius:10, cursor:'pointer', fontSize:12 }}
                              >
                                Löschen
                              </button>
                            </div>
                          </div>

                          {!isEditing ? (
                            <pre style={{ margin:0, whiteSpace:'pre-wrap', fontSize: 13, opacity: 0.9, background:'#0c0c0c', border:'1px solid rgba(255,255,255,0.06)', padding: 10, borderRadius:10 }}>
                              {m.content}
                            </pre>
                          ) : (
                            <div>
                              <div style={{ display:'flex', gap: 10, alignItems:'center', flexWrap:'wrap', marginBottom: 8 }}>
                                <div style={{ fontSize: 12, opacity: 0.75 }}>Event:</div>
                                <select
                                  value={workingMsgEventKey}
                                  onChange={(e) => setWorkingMsgEventKey(e.target.value)}
                                  disabled={busyMsg}
                                  style={{ background:'#111', color:'#fff', border:'1px solid #333', borderRadius:10, padding:'8px 10px', fontSize:12 }}
                                >
                                  {DISCORD_EVENT_OPTIONS.map(o => (
                                    <option key={o.key} value={o.key}>{o.label}</option>
                                  ))}
                                </select>
                              </div>
                              <input
                                type="text"
                                value={workingMsgTitle}
                                onChange={(e)=> setWorkingMsgTitle(e.target.value)}
                                placeholder="Titel (optional)"
                                style={{ width:'100%', background:'#111', color:'#fff', border:'1px solid #333', borderRadius:10, padding:'10px 12px', fontSize:13, marginBottom: 8 }}
                              />
                              <FormatToolbar
                                disabled={busyMsg}
                                onAction={(action) => runFormatForTextarea(workingMsgContentRef, workingMsgContent, setWorkingMsgContent, action)}
                              />
                              <PlaceholderToolbar
                                disabled={busyMsg}
                                onInsert={(token) => runInsertForTextarea(workingMsgContentRef, workingMsgContent, setWorkingMsgContent, token)}
                                items={PLACEHOLDERS_BY_EVENT[workingMsgEventKey] || []}
                              />
                              <textarea
                                ref={workingMsgContentRef}
                                value={workingMsgContent}
                                onChange={(e)=> setWorkingMsgContent(e.target.value)}
                                rows={5}
                                style={{ width:'100%', background:'#111', color:'#fff', border:'1px solid #333', borderRadius:10, padding:'10px 12px', fontSize:13, resize:'vertical' }}
                              />

                              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, opacity: 0.9 }}>
                                <input
                                  type="checkbox"
                                  checked={!!workingMsgAppendHere}
                                  onChange={(e) => setWorkingMsgAppendHere(e.target.checked)}
                                  disabled={busyMsg}
                                />
                                @here anhängen (mit Leerzeile davor)
                              </label>
                              <div style={{ display:'flex', gap: 10, marginTop: 10 }}>
                                <button
                                  type="button"
                                  onClick={saveEditMessage}
                                  disabled={busyMsg || !(workingMsgContent || '').trim()}
                                  style={{ background:'#2b2b2b', color:'#fff', border:'1px solid #444', borderRadius:10, padding:'10px 14px', cursor:'pointer' }}
                                >
                                  {busyMsg ? 'Bitte warten…' : 'Speichern'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
